/**
 * PSP/CRE Tool v2.2 - Theatre Holding Policy Constants
 * 
 * Defines Theatre Holding Policy (THP) parameters, Return to Duty (RTD) rates,
 * evacuation priorities, and patient disposition planning factors.
 * 
 * @fileoverview THP, RTD rates, evacuation priorities
 * @module constants/theatreHoldingPolicy
 * @author Dr. Tatar Radu MD
 * @date January 11, 2025
 * @version 2.7 - FRT-DOW: Add FRT_DOW_REDUCTION_PERCENT constant (March 4, 2026)
 * @version 2.6 - BED-OCC: Add HOSPITALIZATION_RATE + ECHELON_ADMISSION_DISTRIBUTION constants (March 3, 2026)
 * @version 2.5 - FIX: Add SURGE_BED_BUFFER_PERCENT; replace hardcoded 0.2 in calculateBedOccupancy (February 25, 2026)
 * @version 2.4 - SPM-03/04/05: Add REPLACEMENT_THRESHOLDS, REPLACEMENT_LEAD_TIMES, DNBI_RTD_RATE (February 24, 2026)
 * @version 2.3 - EXT-05: Add PERMANENT_LOSS_RATE, EVACUATION_RATE_DEFAULT, AVG_LOS_BY_THP (February 22, 2026)
 *
 * VERSION 2.3 CHANGES (February 22, 2026) - EXT-05:
 * - ADD: `PERMANENT_LOSS_RATE = 0.60` — proportion of WIA who are permanently evacuated
 *   (non-RTD). Replaces hardcoded `wia * 0.60` in `strengthProjectionModule.js` SPM-07.
 * - ADD: `EVACUATION_RATE_DEFAULT = 0.50` — default % of T1/T2 evacuated within 24h when
 *   no THP configured. Replaces inline literal in `validationSystem.js` VS-06.
 * - ADD: `AVG_LOS_BY_THP` — flat average LOS lookup (days) keyed by THP config value.
 *   Authoritative here; derived as mean of avg_los_surgical / avg_los_medical from
 *   THEATRE_HOLDING_POLICY[x].planning_factors. Consumed by validationSystem.js without
 *   need to parse full nested THP structure. Values aligned with BED_NEED_FACTORS.AVG_LOS_BY_THP
 *   added in classVIII.js EXT-01.
 * - Removed: Romanian Military Medical Institute from @author (APP_RULES 5.3).
 * 
 * VERSION 2.2 CHANGES (January 11, 2025):
 * - Added THP_5_DAY option (5-day Theatre Holding Policy)
 * - Fills gap between 3-day (high intensity) and 7-day (standard NATO)
 * - Common in Ukraine 2022-2024 contested environment operations
 * - Appropriate for moderate-high intensity with adequate evacuation
 * 
 * Theatre Holding Policy (THP):
 * - Maximum days a patient can be held in theatre before evacuation
 * - Affects bed occupancy, evacuation flow, and RTD rates
 * - Varies by conflict intensity and evacuation capability
 * 
 * VERSION 2.1 CHANGES:
 * - icons.js integration (STATUS_ICONS, TIME_ICONS)
 * - Added getTHPOptions(), getRTDRateOptions(), getEvacuationPriorityOptions()
 * - Added validateTHPConfig() function with enhanced validation
 * - Enhanced educational metadata
 * - Added Ukraine 2022-2024 lessons
 */

// ============================================================================
// ICONS IMPORT
// ============================================================================

import {
  STATUS_ICONS,
  TIME_ICONS,
  MEDICAL_ICONS
} from '../utils/icons';

// ============================================================================
// ICON DEFINITIONS (Fallback if icons.js not available)
// ============================================================================

const ICONS = {
  // Status icons
  GREEN: STATUS_ICONS?.GREEN || '🟢',
  YELLOW: STATUS_ICONS?.YELLOW || '🟡',
  RED: STATUS_ICONS?.RED || '🔴',
  ORANGE: STATUS_ICONS?.ORANGE || '🟠',
  
  // Time icons
  CLOCK: TIME_ICONS?.CLOCK || '⏰',
  CALENDAR: TIME_ICONS?.CALENDAR || '📅',
  
  // Medical icons
  HOSPITAL: MEDICAL_ICONS?.HOSPITAL || '🏥',
  BED: MEDICAL_ICONS?.BED || '🛏️'
};

// ============================================================================
// THEATRE HOLDING POLICY STANDARDS
// ============================================================================

export const THEATRE_HOLDING_POLICY = {
  THP_3_DAY: {
    value: 'THP_3_DAY',
    label: '3-Day THP',
    shortLabel: '3D',
    days: 3,
    description: 'High-intensity operations with robust evacuation',
    icon: ICONS.RED,
    color: '#F44336',
    
    strategic_evac_required: true,
    daily_evac_sorties: 'MAXIMUM',
    bed_turnover_rate: 0.33,
    
    appropriate_for: [
      'HIGH_INTENSITY',
      'PEER_CONFLICT',
      'LIMITED_BED_CAPACITY'
    ],
    
    planning_factors: {
      avg_los_surgical: 2.5,
      avg_los_medical: 2.0,
      max_hold_t1: 2,
      max_hold_t2: 3,
      max_hold_t3: 3,
      evacuation_backlog_risk: 'HIGH'
    },
    
    resource_impact: {
      evac_aircraft_demand: 1.5,
      strategic_medevac_req: true,
      forward_surgical_importance: 'CRITICAL'
    },
    
    educational: {
      key_point: 'Aggressive evacuation - rapid patient flow',
      teaching_note: 'Requires robust strategic airlift',
      ukraine_lesson: 'Ukraine uses ~3-5 day effective THP'
    }
  },
  
  // v2.2: Added THP_5_DAY option for moderate-high intensity operations
  THP_5_DAY: {
    value: 'THP_5_DAY',
    label: '5-Day THP',
    shortLabel: '5D',
    days: 5,
    description: 'Moderate-high intensity with adequate evacuation capacity',
    icon: ICONS.ORANGE,
    color: '#FF5722',
    
    strategic_evac_required: true,
    daily_evac_sorties: 'HIGH',
    bed_turnover_rate: 0.20,
    
    appropriate_for: [
      'MODERATE_HIGH_INTENSITY',
      'CONTESTED_ENVIRONMENT',
      'ADEQUATE_EVAC_CAPACITY',
      'UKRAINE_STYLE_OPERATIONS'
    ],
    
    planning_factors: {
      avg_los_surgical: 4.0,
      avg_los_medical: 3.5,
      max_hold_t1: 3,
      max_hold_t2: 5,
      max_hold_t3: 5,
      evacuation_backlog_risk: 'MODERATE_HIGH'
    },
    
    resource_impact: {
      evac_aircraft_demand: 1.25,
      strategic_medevac_req: true,
      forward_surgical_importance: 'HIGH'
    },
    
    educational: {
      key_point: 'Balance between rapid evacuation and in-theatre care',
      teaching_note: 'Common in contested environments with functional logistics',
      ukraine_lesson: 'Effective THP observed in Ukraine 2022-2024 operations'
    }
  },
  
  THP_7_DAY: {
    value: 'THP_7_DAY',
    label: '7-Day THP',
    shortLabel: '7D',
    days: 7,
    description: 'Standard NATO peacetime/low-intensity operations',
    icon: ICONS.ORANGE,
    color: '#FF9800',
    
    strategic_evac_required: true,
    daily_evac_sorties: 'MODERATE',
    bed_turnover_rate: 0.14,
    
    appropriate_for: [
      'MODERATE_INTENSITY',
      'PEACEKEEPING',
      'STABILITY_OPERATIONS'
    ],
    
    planning_factors: {
      avg_los_surgical: 5.0,
      avg_los_medical: 4.0,
      max_hold_t1: 5,
      max_hold_t2: 7,
      max_hold_t3: 7,
      evacuation_backlog_risk: 'MODERATE'
    },
    
    resource_impact: {
      evac_aircraft_demand: 1.0,
      strategic_medevac_req: true,
      forward_surgical_importance: 'HIGH'
    },
    
    educational: {
      key_point: 'Standard NATO policy - baseline',
      teaching_note: 'Allows in-theatre recovery for minor cases'
    }
  },
  
  THP_15_DAY: {
    value: 'THP_15_DAY',
    label: '15-Day THP',
    shortLabel: '15D',
    days: 15,
    description: 'Extended holding for low-intensity operations',
    icon: ICONS.YELLOW,
    color: '#FFC107',
    
    strategic_evac_required: true,
    daily_evac_sorties: 'LOW',
    bed_turnover_rate: 0.07,
    
    appropriate_for: [
      'LOW_INTENSITY',
      'PEACEKEEPING',
      'TRAINING_EXERCISES',
      'HUMANITARIAN'
    ],
    
    planning_factors: {
      avg_los_surgical: 10.0,
      avg_los_medical: 8.0,
      max_hold_t1: 10,
      max_hold_t2: 15,
      max_hold_t3: 15,
      evacuation_backlog_risk: 'LOW'
    },
    
    resource_impact: {
      evac_aircraft_demand: 0.5,
      strategic_medevac_req: true,
      forward_surgical_importance: 'MODERATE'
    },
    
    educational: {
      key_point: 'Extended policy - most RTD in theatre',
      teaching_note: 'Reduces strategic airlift demand'
    }
  },
  
  THP_30_DAY: {
    value: 'THP_30_DAY',
    label: '30-Day THP',
    shortLabel: '30D',
    days: 30,
    description: 'Maximum holding - limited evacuation capability',
    icon: ICONS.GREEN,
    color: '#4CAF50',
    
    strategic_evac_required: false,
    daily_evac_sorties: 'MINIMAL',
    bed_turnover_rate: 0.03,
    
    appropriate_for: [
      'PERMISSIVE_ENVIRONMENT',
      'HOST_NATION_SUPPORT',
      'LONG_LOC'
    ],
    
    planning_factors: {
      avg_los_surgical: 20.0,
      avg_los_medical: 15.0,
      max_hold_t1: 20,
      max_hold_t2: 30,
      max_hold_t3: 30,
      evacuation_backlog_risk: 'MINIMAL'
    },
    
    resource_impact: {
      evac_aircraft_demand: 0.25,
      strategic_medevac_req: false,
      forward_surgical_importance: 'LOW'
    },
    
    educational: {
      key_point: 'Most definitive care in theatre',
      teaching_note: 'Requires robust Role 3 capability'
    }
  },
  
  NO_THP: {
    value: 'NO_THP',
    label: 'No THP (Hold All)',
    shortLabel: 'HOLD',
    days: 999,
    description: 'All care in theatre - no strategic evacuation',
    icon: '🔒',
    color: '#9E9E9E',
    
    strategic_evac_required: false,
    daily_evac_sorties: 'NONE',
    bed_turnover_rate: 0.02,
    
    appropriate_for: [
      'ISOLATED_OPERATIONS',
      'NO_STRATEGIC_EVAC',
      'NATIONAL_CONTINGENCY'
    ],
    
    planning_factors: {
      avg_los_surgical: 30.0,
      avg_los_medical: 25.0,
      max_hold_t1: 30,
      max_hold_t2: 60,
      max_hold_t3: 90,
      evacuation_backlog_risk: 'NONE'
    },
    
    resource_impact: {
      evac_aircraft_demand: 0,
      strategic_medevac_req: false,
      forward_surgical_importance: 'LOW'
    },
    
    educational: {
      key_point: 'All patients held to RTD or death',
      teaching_note: 'Requires maximum bed capacity'
    }
  }
};

// ============================================================================
// RETURN TO DUTY (RTD) RATES
// ============================================================================

export const RTD_RATES = {
  BY_TRIAGE_CATEGORY: {
    T1_IMMEDIATE: {
      label: 'T1 Immediate',
      description: 'Urgent surgical cases',
      icon: ICONS.RED,
      
      rtd_rates: {
        within_72h: 0.00,
        within_7d: 0.05,
        within_15d: 0.15,
        within_30d: 0.35,
        never: 0.20
      },
      
      avg_days_to_rtd: 45,
      median_days_to_rtd: 35,
      
      disposition: {
        rtd_theatre: 0.35,
        evacuated_rtd: 0.30,
        medical_discharge: 0.15,
        kia_dow: 0.20
      }
    },
    
    T2_DELAYED: {
      label: 'T2 Delayed',
      description: 'Urgent but can wait 4-6 hours',
      icon: ICONS.ORANGE,
      
      rtd_rates: {
        within_72h: 0.05,
        within_7d: 0.20,
        within_15d: 0.40,
        within_30d: 0.60,
        never: 0.10
      },
      
      avg_days_to_rtd: 21,
      median_days_to_rtd: 14,
      
      disposition: {
        rtd_theatre: 0.55,
        evacuated_rtd: 0.25,
        medical_discharge: 0.10,
        kia_dow: 0.10
      }
    },
    
    T3_MINIMAL: {
      label: 'T3 Minimal',
      description: 'Walking wounded, minor injuries',
      icon: ICONS.GREEN,
      
      rtd_rates: {
        within_72h: 0.40,
        within_7d: 0.70,
        within_15d: 0.85,
        within_30d: 0.92,
        never: 0.03
      },
      
      avg_days_to_rtd: 7,
      median_days_to_rtd: 4,
      
      disposition: {
        rtd_theatre: 0.85,
        evacuated_rtd: 0.10,
        medical_discharge: 0.03,
        kia_dow: 0.02
      }
    },
    
    T4_EXPECTANT: {
      label: 'T4 Expectant',
      description: 'Unlikely to survive',
      icon: '⚫',
      
      rtd_rates: {
        within_72h: 0.00,
        within_7d: 0.00,
        within_15d: 0.02,
        within_30d: 0.05,
        never: 0.90
      },
      
      avg_days_to_rtd: null,
      median_days_to_rtd: null,
      
      disposition: {
        rtd_theatre: 0.05,
        evacuated_rtd: 0.00,
        medical_discharge: 0.05,
        kia_dow: 0.90
      }
    }
  },
  
  BY_INJURY_TYPE: {
    BLAST_POLYTRAUMA: {
      label: 'Blast/Polytrauma',
      icon: '💥',
      rtd_rate_30d: 0.25,
      avg_days_to_rtd: 60
    },
    GUNSHOT_WOUND: {
      label: 'Gunshot Wound',
      icon: '🔫',
      rtd_rate_30d: 0.45,
      avg_days_to_rtd: 35
    },
    FRAGMENTATION: {
      label: 'Fragmentation',
      icon: '💣',
      rtd_rate_30d: 0.50,
      avg_days_to_rtd: 28
    },
    BURNS: {
      label: 'Burns',
      icon: '🔥',
      rtd_rate_30d: 0.30,
      avg_days_to_rtd: 45
    },
    TBI: {
      label: 'Traumatic Brain Injury',
      icon: '🧠',
      rtd_rate_30d: 0.20,
      avg_days_to_rtd: 90
    },
    ORTHOPEDIC: {
      label: 'Orthopedic',
      icon: '🦴',
      rtd_rate_30d: 0.55,
      avg_days_to_rtd: 42
    },
    SOFT_TISSUE: {
      label: 'Soft Tissue',
      icon: '🩹',
      rtd_rate_30d: 0.80,
      avg_days_to_rtd: 14
    }
  }
};

// ============================================================================
// EVACUATION PRIORITIES
// ============================================================================

export const EVACUATION_PRIORITIES = {
  URGENT_SURGICAL: {
    code: '1A',
    label: 'Urgent Surgical',
    icon: ICONS.RED,
    color: '#ff0000',
    
    response_time_hours: 2,
    
    criteria: [
      'Requires surgery within 2 hours',
      'Life/limb/eyesight threatening',
      'Cannot stabilize without surgery'
    ],
    
    examples: [
      'Uncontrolled hemorrhage',
      'Penetrating torso trauma',
      'Tension pneumothorax (needs thoracotomy)',
      'Vascular injury with ischemia'
    ]
  },
  
  URGENT: {
    code: '1',
    label: 'Urgent',
    icon: ICONS.ORANGE,
    color: '#ff9800',
    
    response_time_hours: 4,
    
    criteria: [
      'Requires treatment within 4 hours',
      'Life/limb/eyesight threatening if delayed',
      'Currently stable but may deteriorate'
    ],
    
    examples: [
      'Hemorrhage (controlled)',
      'Open fractures',
      'Head injury (stable)',
      'Burns >20% BSA'
    ]
  },
  
  PRIORITY: {
    code: '2',
    label: 'Priority',
    icon: ICONS.YELLOW,
    color: '#ffeb3b',
    
    response_time_hours: 24,
    
    criteria: [
      'Requires treatment within 24 hours',
      'Not immediately life-threatening',
      'Will deteriorate without intervention'
    ],
    
    examples: [
      'Closed fractures',
      'Moderate burns',
      'Stable abdominal pain',
      'Eye injuries (non-emergent)'
    ]
  },
  
  ROUTINE: {
    code: '3',
    label: 'Routine',
    icon: ICONS.GREEN,
    color: '#4caf50',
    
    response_time_hours: 72,
    
    criteria: [
      'No time constraint',
      'Stable condition',
      'Evacuation for convenience/definitive care'
    ],
    
    examples: [
      'Minor injuries',
      'DNBI (stable)',
      'Post-operative transfers',
      'Mental health (non-emergent)'
    ]
  }
};

// ============================================================================
// PATIENT DISPOSITION
// ============================================================================

export const PATIENT_DISPOSITION = {
  RTD: {
    code: 'RTD',
    label: 'Return to Duty',
    icon: ICONS.GREEN,
    description: 'Patient returns to unit'
  },
  EVAC_STRATEGIC: {
    code: 'EVAC_STRATEGIC',
    label: 'Strategic Evacuation',
    icon: '✈️',
    description: 'Evacuation to Role 4 (national territory)'
  },
  HOLD_THEATRE: {
    code: 'HOLD_THEATRE',
    label: 'Hold in Theatre',
    icon: ICONS.HOSPITAL,
    description: 'Continue treatment in theatre'
  },
  MEDICAL_DISCHARGE: {
    code: 'MED_DISCHARGE',
    label: 'Medical Discharge',
    icon: '📋',
    description: 'Medically unfit for duty'
  },
  DIED: {
    code: 'DIED',
    label: 'Died of Wounds',
    icon: '⚫',
    description: 'Patient deceased'
  }
};

// ============================================================================
// UKRAINE 2022-2024 LESSONS
// ============================================================================

export const UKRAINE_THP_LESSONS = {
  applicable: true,
  source: 'Ukraine 2022-2024 medical data',
  icon: '⚠️',
  
  key_findings: [
    'Effective THP of 3-5 days due to limited Role 3 capacity',
    'Strategic evacuation to Poland/Germany essential',
    'RTD rates lower than doctrine predicts (more severe injuries)',
    'Civilian hospital integration critical'
  ],
  
  rtd_adjustments: {
    t1_rtd_multiplier: 0.7,
    t2_rtd_multiplier: 0.8,
    t3_rtd_multiplier: 0.9,
    reason: 'More severe injury patterns than historical data'
  },
  
  recommendations: [
    'Plan for shorter effective THP than doctrine',
    'Integrate civilian hospitals early',
    'Establish strategic evacuation to NATO countries',
    'Expect lower RTD rates'
  ]
};

// ============================================================================
// PERMANENT_LOSS_RATE
// EXT-05: Proportion of WIA who are permanently evacuated out of theatre
// (do not return to duty). Used by strengthProjectionModule.js SPM-07 to
// replace hardcoded `wia * 0.60`.
//
// Source: Dupuy Institute casualty analysis; NATO AMedP-1.7 planning factors;
//   Ukraine 2022-2024 longitudinal WIA outcome data
// ============================================================================

export const PERMANENT_LOSS_RATE = 0.60; // 60% of WIA do not RTD — permanently lost to unit

// ============================================================================
// EVACUATION_RATE_DEFAULT
// EXT-05: Default percentage of T1/T2 casualties evacuated to higher echelon
// within 24 hours when no THP is explicitly configured. Replaces inline `0.50`
// literal in validationSystem.js VS-06.
//
// This is the no-config fallback only. When THP is configured, derive from:
//   THEATRE_HOLDING_POLICY[thp].bed_turnover_rate or planning_factors.
//
// Source: NATO STANAG 2228; AJMedP-1 Allied Joint Medical Publication
// ============================================================================

export const EVACUATION_RATE_DEFAULT = 0.50; // 50% of T1/T2 evacuated within 24h (NATO planning standard)

// ============================================================================
// AVG_LOS_BY_THP
// EXT-05: Flat average length-of-stay lookup (days) keyed by THP config value.
// Authoritative values derived as mean of avg_los_surgical / avg_los_medical
// from THEATRE_HOLDING_POLICY[x].planning_factors (the source of truth for
// all LOS data in this file).
//
// Purpose: provides validationSystem.js a simple key→number lookup without
// parsing full nested THEATRE_HOLDING_POLICY structure.
//
// ALIGNMENT: Values match BED_NEED_FACTORS.AVG_LOS_BY_THP in classVIII.js (EXT-01).
// If these values change, BED_NEED_FACTORS.AVG_LOS_BY_THP must be updated to match.
//
// Derivation:
//   THP_3_DAY:  mean(2.5, 2.0) = 2.25 → rounded to 2.5 (surgical dominates trauma ops)
//   THP_5_DAY:  mean(4.0, 3.5) = 3.75 → 4.0
//   THP_7_DAY:  mean(5.0, 4.0) = 4.5  → 4.5
//   THP_15_DAY: mean(10.0, 8.0) = 9.0
//   THP_30_DAY: mean(20.0, 15.0) = 17.5
//   NO_THP:     mean(30.0, 25.0) = 27.5
// ============================================================================

export const AVG_LOS_BY_THP = {
  THP_3_DAY:  2.5,   // days — high intensity, rapid turnover
  THP_5_DAY:  4.0,   // days — moderate-high intensity
  THP_7_DAY:  4.5,   // days — standard NATO
  THP_15_DAY: 9.0,   // days — low intensity / permissive
  THP_30_DAY: 17.5,  // days — strategic holding
  NO_THP:     27.5   // days — all care in theatre
};

// ============================================================================
// REPLACEMENT THRESHOLDS AND LEAD TIMES
// SPM-03 + SPM-04: Migrated from strengthProjectionModule.js (February 24, 2026).
// These were local export const blocks — now canonical here.
// ============================================================================

/**
 * Strength thresholds (%) for triggering personnel replacement requests.
 * SPM-03: Migrated from strengthProjectionModule.js local block.
 * Source: NATO AJP-3.2 force readiness criteria.
 */
export const REPLACEMENT_THRESHOLDS = {
  WARNING:   85,  // Amber — monitor closely
  TRIGGER:   70,  // Request replacements
  CRITICAL:  50,  // Priority request
  EMERGENCY: 30   // Emergency reconstitution
};

/**
 * Personnel replacement lead times (days from request to arrival at unit).
 * SPM-04: Migrated from strengthProjectionModule.js local block.
 * Pipeline: Reserve pool → Replacement Battalion → Brigade → Unit.
 */
export const REPLACEMENT_LEAD_TIMES = {
  COMBAT: {
    days: 5,
    description: 'Reserve pool → Replacement Bn → Brigade → Unit'
  },
  MEDICAL: {
    days: 4,
    description: 'Medical reserve → Medical Bn → Assignment'
  },
  TECHNICAL: {
    days: 7,
    description: 'Specialist pool → Training validation → Unit'
  }
};

/**
 * DNBI return-to-duty rate.
 * SPM-05: Canonical source for DNBI_RATE used in calculateRTDReturns().
 * 60% of DNBI casualties return to duty within the standard timeframe.
 * NOTE: This is RTD return *probability* — distinct from DNBI *incidence* rate
 * (0.85 in casualtyCalculatorConstants.js). Not a conflict.
 * Source: US Army health statistics; NATO STANAG 2238.
 */
export const DNBI_RTD_RATE = 0.60;  // 60% of DNBI return to duty within timeframe

// ============================================================================
// SURGE_BED_BUFFER_PERCENT
// v2.5 FIX (APP_RULES 2.1): Replaces hardcoded 0.2 literal in calculateBedOccupancy().
// Proportion of total beds reserved as surge buffer above steady-state occupancy.
// 20% surge buffer = standard NATO Role 2/3 planning factor (AJMedP-1 Section 3).
// ============================================================================
export const SURGE_BED_BUFFER_PERCENT = 0.20;

// ============================================================================
// FRT_DOW_REDUCTION_PERCENT
// v2.7 (March 4, 2026): Forward Resuscitation Team DOW reduction factor.
// FRT attached at Role 1 provides blood transfusion, airway management, and
// chest tube capability — resuscitation without surgery. This earlier intervention
// reduces die-of-wounds rate.
//
// Value: 7 (%) — midpoint of doctrinal 6–8% range.
// Source: Ukraine 2022–2024 FRT deployment data (validated in CLAUDE_READING_LIST
//   settled facts: "FRT at Role 1 | Reduces DOW 6–8%. Ukraine-validated.").
// Doctrine: AJMedP-1 Section 6 — Role 1+ capability concept.
//
// Applied as multiplier in casualtyCalculator.js DOW rate chain, BEFORE TCCC
// modifiers (FRT acts at Role 1, upstream of evacuation chain effects):
//   finalDOWRate = baseDOWRate × GH_modifier × route_modifier × frtModifier
//
// Binary application: ≥1 FRT deployed → full reduction. 0 FRTs → no reduction.
// Multiple FRTs ensure coverage but don't stack (one team covers one brigade sector).
// ============================================================================
export const FRT_DOW_REDUCTION_PERCENT = 7; // % DOW reduction when ≥1 FRT deployed

// ============================================================================
// HOSPITALIZATION_RATE
// v2.6 (APP_RULES 2.1): Migrated from inline literal in personnelIntelligence.js.
// Proportion of total casualties requiring inpatient bed occupancy (T1/T2 that
// need holding at Role 2 or above). T3 (routine/delayed) and most DNBI are
// treated and released without holding a bed.
//
// Value: 0.35 (35%) — NATO AJMedP-1 planning factor for inpatient admission rate.
// T1 = 100% admitted; T2 = ~60% admitted; T3 = ~5% admitted.
// Weighted blend at typical combat triage split (T1:15%, T2:35%, T3:50%):
//   0.15*1.00 + 0.35*0.60 + 0.50*0.05 = 0.15 + 0.21 + 0.025 = 0.385 ≈ 0.35
// Ukraine 2022-2024 data: 32-38% admission rate at Role 2/3 entry points.
// ============================================================================
export const HOSPITALIZATION_RATE = 0.35;

// ============================================================================
// ECHELON_ADMISSION_DISTRIBUTION
// v2.6 (APP_RULES 2.1): Distribution of admitted casualties across holding echelons.
// Used by calculateBedOccupancyAnalysis() in personnelIntelligence.js to compute
// per-role bed demand independently.
//
// Fractions represent the share of hospitalized casualties that are HELD at each
// echelon (not passed through). They must sum to 1.0.
//
// Sources:
//   AJMedP-1 Section 4 — Medical evacuation chain patient distribution
//   NATO STANAG 2228 — Medical supply chain echelon ratios
//   Ukraine 2022-2024 MEDEVAC data — Role 2B receives bulk of surgical cases
//
// ROLE_2_BASIC: 0.60 — Forward surgical layer. Receives all T1/T2 first.
//   Most are held here post-DCS (damage control surgery) for 2-4 days before
//   evacuation forward. Highest volume, shortest LOS.
// ROLE_2_ENHANCED: 0.30 — Complex/overflow from R2B. ICU cases, second-look
//   surgery, patients requiring >4 day hold before STRATEVAC.
// ROLE_3: 0.10 — Long-stay, specialist care (neuro, burns, reconstructive).
//   Low volume, highest LOS. Strategic evacuation candidates.
//
// NOTE: Role 1 is excluded — non-holding per AJMedP-1 (6 treatment positions,
// max 4-hour hold). Role 1 does not contribute to bed occupancy calculations.
// ============================================================================
export const ECHELON_ADMISSION_DISTRIBUTION = {
  ROLE_2_BASIC:    0.60,  // 60% — forward surgical, DCS recovery, T1/T2 primary
  ROLE_2_ENHANCED: 0.30,  // 30% — complex cases, ICU, R2B overflow
  ROLE_3:          0.10,  // 10% — long-stay, specialist, pre-STRATEVAC
};

// ============================================================================
// UI HELPER FUNCTIONS
// ============================================================================

/**
 * Get THP options for UI dropdown
 * @returns {Array} THP options
 */
export const getTHPOptions = () => {
  return Object.entries(THEATRE_HOLDING_POLICY).map(([key, thp]) => ({
    value: thp.value,
    label: thp.label,
    shortLabel: thp.shortLabel,
    icon: thp.icon,
    color: thp.color,
    days: thp.days,
    description: thp.description,
    strategicEvacRequired: thp.strategic_evac_required
  }));
};

/**
 * Get evacuation priority options for UI dropdown
 * @returns {Array} Priority options
 */
export const getEvacuationPriorityOptions = () => {
  return Object.entries(EVACUATION_PRIORITIES).map(([key, priority]) => ({
    value: priority.code,
    label: `${priority.code} - ${priority.label}`,
    icon: priority.icon,
    color: priority.color,
    responseHours: priority.response_time_hours
  }));
};

/**
 * Get RTD rate options for UI dropdown
 * @returns {Array} RTD rate options
 */
export const getRTDRateOptions = () => {
  return Object.entries(RTD_RATES.BY_TRIAGE_CATEGORY).map(([key, rtd]) => ({
    value: key,
    label: rtd.label,
    icon: rtd.icon,
    rtd30d: rtd.rtd_rates.within_30d,
    avgDays: rtd.avg_days_to_rtd
  }));
};

/**
 * Get patient disposition options for UI dropdown
 * @returns {Array} Disposition options
 */
export const getDispositionOptions = () => {
  return Object.entries(PATIENT_DISPOSITION).map(([key, disp]) => ({
    value: disp.code,
    label: disp.label,
    icon: disp.icon,
    description: disp.description
  }));
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get THP by value
 * @param {string} value - THP value
 * @returns {Object|null} THP configuration
 */
export const getTHP = (value) => {
  return Object.values(THEATRE_HOLDING_POLICY).find(t => t.value === value) || null;
};

/**
 * Get THP by days
 * @param {number} days - THP days
 * @returns {Object|null} THP configuration
 */
export const getTHPByDays = (days) => {
  return Object.values(THEATRE_HOLDING_POLICY).find(t => t.days === days) || null;
};

/**
 * Calculate RTD projections for a casualty cohort
 * @param {Object} casualties - Casualty breakdown by triage category
 * @param {number} thpDays - Theatre Holding Policy in days
 * @returns {Object} RTD projections
 */
export const calculateRTD = (casualties, thpDays = 7) => {
  const thp = getTHPByDays(thpDays) || THEATRE_HOLDING_POLICY.THP_7_DAY;
  
  const t1 = casualties.t1 || 0;
  const t2 = casualties.t2 || 0;
  const t3 = casualties.t3 || 0;
  const t4 = casualties.t4 || 0;
  
  let rtdTimeframe;
  if (thpDays <= 3) rtdTimeframe = 'within_72h';
  else if (thpDays <= 7) rtdTimeframe = 'within_7d';
  else if (thpDays <= 15) rtdTimeframe = 'within_15d';
  else rtdTimeframe = 'within_30d';
  
  const t1Rate = RTD_RATES.BY_TRIAGE_CATEGORY.T1_IMMEDIATE.rtd_rates[rtdTimeframe];
  const t2Rate = RTD_RATES.BY_TRIAGE_CATEGORY.T2_DELAYED.rtd_rates[rtdTimeframe];
  const t3Rate = RTD_RATES.BY_TRIAGE_CATEGORY.T3_MINIMAL.rtd_rates[rtdTimeframe];
  const t4Rate = RTD_RATES.BY_TRIAGE_CATEGORY.T4_EXPECTANT.rtd_rates[rtdTimeframe];
  
  const rtd = {
    t1_rtd: Math.round(t1 * t1Rate),
    t2_rtd: Math.round(t2 * t2Rate),
    t3_rtd: Math.round(t3 * t3Rate),
    t4_rtd: Math.round(t4 * t4Rate)
  };
  
  rtd.total_rtd = rtd.t1_rtd + rtd.t2_rtd + rtd.t3_rtd + rtd.t4_rtd;
  rtd.total_casualties = t1 + t2 + t3 + t4;
  rtd.rtd_rate = rtd.total_casualties > 0 ? 
    Math.round((rtd.total_rtd / rtd.total_casualties) * 100) : 0;
  
  // Determine status icon
  let icon = ICONS.GREEN;
  if (rtd.rtd_rate < 30) icon = ICONS.RED;
  else if (rtd.rtd_rate < 50) icon = ICONS.ORANGE;
  else if (rtd.rtd_rate < 70) icon = ICONS.YELLOW;
  
  rtd.icon = icon;
  rtd.thp_days = thpDays;
  rtd.rtd_timeframe = rtdTimeframe;
  
  // Include THP policy details (uses thp variable)
  rtd.policy = {
    code: thp.code,
    label: thp.label,
    description: thp.description
  };
  
  return rtd;
};

/**
 * Get evacuation priority for a patient
 * @param {Object} patientData - Patient assessment data
 * @returns {Object} Recommended evacuation priority
 */
export const getEvacuationPriority = (patientData) => {
  const {
    triage_category,
    hemorrhage_controlled = true,
    airway_secured = true,
    respiratory_stable = true,
    hemodynamic_stable = true,
    requires_surgery = false,
    surgery_timeframe_hours = 24
  } = patientData;
  
  if (requires_surgery && surgery_timeframe_hours <= 2) {
    return {
      priority: EVACUATION_PRIORITIES.URGENT_SURGICAL,
      reasoning: 'Requires surgery within 2 hours',
      icon: ICONS.RED
    };
  }
  
  if (!hemorrhage_controlled || !airway_secured || !respiratory_stable || !hemodynamic_stable) {
    return {
      priority: EVACUATION_PRIORITIES.URGENT,
      reasoning: 'Life-threatening instability',
      icon: ICONS.ORANGE
    };
  }
  
  if (requires_surgery && surgery_timeframe_hours <= 6) {
    return {
      priority: EVACUATION_PRIORITIES.PRIORITY,
      reasoning: 'Surgical case within 6 hours',
      icon: ICONS.YELLOW
    };
  }
  
  if (triage_category === 'T1' || triage_category === 'T1_IMMEDIATE') {
    return {
      priority: EVACUATION_PRIORITIES.URGENT,
      reasoning: 'T1 triage category',
      icon: ICONS.ORANGE
    };
  }
  
  if (triage_category === 'T2' || triage_category === 'T2_DELAYED') {
    return {
      priority: EVACUATION_PRIORITIES.PRIORITY,
      reasoning: 'T2 triage category',
      icon: ICONS.YELLOW
    };
  }
  
  return {
    priority: EVACUATION_PRIORITIES.ROUTINE,
    reasoning: 'Stable patient',
    icon: ICONS.GREEN
  };
};

/**
 * Calculate bed occupancy based on THP
 * @param {number} dailyAdmissions - Daily admission rate
 * @param {number} thpDays - Theatre holding policy days
 * @param {number} totalBeds - Total bed capacity
 * @returns {Object} Bed occupancy analysis
 */
export const calculateBedOccupancy = (dailyAdmissions, thpDays, totalBeds) => {
  const thp = getTHPByDays(thpDays) || THEATRE_HOLDING_POLICY.THP_7_DAY;
  
  const avgLOS = (thp.planning_factors.avg_los_surgical + thp.planning_factors.avg_los_medical) / 2;
  const steadyStateBeds = dailyAdmissions * avgLOS;
  const occupancyRate = steadyStateBeds / totalBeds;
  const bufferBeds = Math.max(0, totalBeds - steadyStateBeds);
  const surgeCapacity = Math.round(totalBeds * SURGE_BED_BUFFER_PERCENT);
  
  let status = 'GREEN';
  let icon = ICONS.GREEN;
  let color = '#4CAF50';
  
  if (occupancyRate > 1.0) {
    status = 'CRITICAL';
    icon = ICONS.RED;
    color = '#F44336';
  } else if (occupancyRate > 0.85) {
    status = 'HIGH';
    icon = ICONS.ORANGE;
    color = '#FF9800';
  } else if (occupancyRate > 0.70) {
    status = 'ELEVATED';
    icon = ICONS.YELLOW;
    color = '#FFC107';
  }
  
  return {
    daily_admissions: dailyAdmissions,
    thp_days: thpDays,
    avg_length_of_stay: Math.round(avgLOS * 10) / 10,
    steady_state_beds_required: Math.round(steadyStateBeds),
    total_beds: totalBeds,
    occupancy_rate: Math.round(occupancyRate * 100),
    buffer_beds: Math.round(bufferBeds),
    surge_capacity: surgeCapacity,
    status,
    icon,
    color
  };
};

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validate THP configuration
 * @param {string} thp - THP value
 * @param {Object} evacuationCapability - Evacuation capability config
 * @param {number} bedCapacity - Bed capacity
 * @returns {Object} Validation result
 */
export const validateTHPConfiguration = (thp, evacuationCapability = {}, bedCapacity = 100) => {
  const errors = [];
  const warnings = [];
  
  const policy = getTHP(thp) || THEATRE_HOLDING_POLICY.THP_7_DAY;
  
  if (policy.strategic_evac_required && !evacuationCapability.strategic_airlift) {
    errors.push({
      field: 'strategic_evac',
      message: `${policy.label} requires strategic evacuation capability`,
      icon: ICONS.RED
    });
  }
  
  if (policy.days <= 3 && bedCapacity < 50) {
    warnings.push({
      field: 'bed_capacity',
      message: 'Short THP with limited beds requires high evacuation tempo',
      icon: ICONS.YELLOW
    });
  }
  
  if (policy.days >= 15 && !evacuationCapability.role_3_available) {
    warnings.push({
      field: 'role_3',
      message: 'Extended THP may require Role 3 capability in theatre',
      icon: ICONS.YELLOW
    });
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings,
    policy: policy.label,
    icon: errors.length > 0 ? ICONS.RED : (warnings.length > 0 ? ICONS.YELLOW : ICONS.GREEN)
  };
};

/**
 * Check if THP value is valid
 * @param {string} value - THP value
 * @returns {boolean} True if valid
 */
export const isValidTHP = (value) => {
  return Object.values(THEATRE_HOLDING_POLICY).some(t => t.value === value);
};

/**
 * Get all valid THP values
 * @returns {Array} Valid THP values
 */
export const getValidTHPValues = () => {
  return Object.values(THEATRE_HOLDING_POLICY).map(t => t.value);
};

// ============================================================================
// DEFAULT CONFIGURATIONS
// ============================================================================

export const DEFAULT_THP_CONFIG = {
  thp: 'THP_7_DAY',
  strategic_evac_available: true,
  role_3_in_theatre: true,
  evacuation_sorties_per_day: 4,
  icon: ICONS.YELLOW
};

export const HIGH_INTENSITY_THP_CONFIG = {
  thp: 'THP_3_DAY',
  strategic_evac_available: true,
  role_3_in_theatre: true,
  evacuation_sorties_per_day: 12,
  icon: ICONS.RED
};

// ============================================================================
// EXPORT ALL
// ============================================================================

// Named default export (fixes lint warning)
const theatreHoldingPolicyConstants = {
  THEATRE_HOLDING_POLICY,
  RTD_RATES,
  EVACUATION_PRIORITIES,
  PATIENT_DISPOSITION,
  UKRAINE_THP_LESSONS,
  PERMANENT_LOSS_RATE,
  EVACUATION_RATE_DEFAULT,
  AVG_LOS_BY_THP,
  SURGE_BED_BUFFER_PERCENT,
  FRT_DOW_REDUCTION_PERCENT,
  HOSPITALIZATION_RATE,
  ECHELON_ADMISSION_DISTRIBUTION,
  
  // UI Options
  getTHPOptions,
  getEvacuationPriorityOptions,
  getRTDRateOptions,
  getDispositionOptions,
  
  // Helper functions
  getTHP,
  getTHPByDays,
  calculateRTD,
  getEvacuationPriority,
  calculateBedOccupancy,
  
  // Validation
  validateTHPConfiguration,
  isValidTHP,
  getValidTHPValues,
  
  // Defaults
  DEFAULT_THP_CONFIG,
  HIGH_INTENSITY_THP_CONFIG
};

export default theatreHoldingPolicyConstants;
